import { buildFullNavTreeData } from 'navigation/rosieNavTreeData';
import type { RosiePermission } from './permissions.types';
import type { RouteAccessRule } from './routeAccess.types';
import {
  ADMIN_ONLY_USER_TYPES,
  ROSIE_SESSION_USER_TYPES,
  STAFF_USER_TYPES,
} from './routeAccess.userTypes';
import { pathPrefixFromHref } from './pathPrefix.util';
import { getSupplementalRouteRuleSeeds } from './supplementalRouteRules';
import type { RosieSessionUserType } from './rosieUserTypes';

const NAV_YEAR = new Date().getFullYear().toString();

function allowedUserTypesForPathPrefix(
  pathPrefix: string,
): readonly RosieSessionUserType[] {
  if (
    pathPrefix.startsWith('/admin') ||
    pathPrefix.startsWith('/emails')
  ) {
    return ADMIN_ONLY_USER_TYPES;
  }
  if (pathPrefix.startsWith('/scla')) {
    return ROSIE_SESSION_USER_TYPES;
  }
  return STAFF_USER_TYPES;
}

function collectNavLeaves(
  nodes: ReturnType<typeof buildFullNavTreeData>,
  out: { id: string; pathPrefix: string }[] = [],
): { id: string; pathPrefix: string }[] {
  nodes.forEach(n => {
    if (n.children?.length) {
      collectNavLeaves(n.children, out);
    } else if (n.to) {
      // Nav-only ids (no backend ROUTE_RULE_IDS entry) still link via pathPrefix
      // matching on a parent rule — omit them from generated route rules.
      if (n.id.startsWith('student-activities-nav-')) {
        return;
      }
      out.push({ id: n.id, pathPrefix: pathPrefixFromHref(n.to) });
    }
  });
  return out;
}

function dedupeById(
  nav: { id: string; pathPrefix: string }[],
  extra: { id: string; pathPrefix: string }[],
): { id: string; pathPrefix: string }[] {
  const byId = new Map<string, { id: string; pathPrefix: string }>();
  extra.forEach(row => {
    byId.set(row.id, row);
  });
  nav.forEach(row => {
    if (!byId.has(row.id)) {
      byId.set(row.id, row);
    }
  });
  return Array.from(byId.values());
}

export default function buildRosieRouteAccessRules(): RouteAccessRule[] {
  const navLeaves = collectNavLeaves(buildFullNavTreeData(NAV_YEAR));
  const seeds = dedupeById(navLeaves, getSupplementalRouteRuleSeeds());

  const fromLeaves = seeds.map(
    (s): RouteAccessRule => ({
      id: s.id,
      pathPrefix: s.pathPrefix,
      allowedUserTypes: allowedUserTypesForPathPrefix(s.pathPrefix),
      readPermission: `${s.id}:read` as RosiePermission,
    }),
  );

  const explicit = [...fromLeaves].sort(
    (a, b) => b.pathPrefix.length - a.pathPrefix.length,
  );

  const staffFallback: RouteAccessRule = {
    id: 'staff',
    pathPrefix: '',
    allowedUserTypes: STAFF_USER_TYPES,
    readPermission: 'profiles-txters:read' as RosiePermission,
  };

  return [...explicit, staffFallback];
}
